/**
 * @fileoverview Pure client-side grouping for the flagged-transactions table.
 * @module jobs/phases/review/flaggedTransactions/flaggedGrouping
 *
 * No React/Convex deps so it's unit-tested directly. "event" mode clusters
 * flags that share an underlying source (overlapping linkedTransactionIds or
 * the same source table+id) — the same real-world event captured by several
 * heads — so the practitioner reads it as ONE recovery, not N.
 */

import { DECISION_BADGE, RISK_BADGE, pursuedAgainst } from "./flaggedShared";
import type { FlagRowUI, FlaggedHeadKey } from "./flaggedShared";

export type GroupByMode =
  | "none"
  | "counterparty"
  | "respondent"
  | "agent"
  | "risk"
  | "decision"
  | "event";

export interface FlagGroup {
  key: string;
  label: string;
  headKeys: FlaggedHeadKey[];
  rows: FlagRowUI[];
  amount: number;
  recoverable: number;
}

const recoverableOf = (r: FlagRowUI) => r.quantum ?? r.amount;

/** Stable ids a flag is "about" — shared ids mean the same underlying event. */
function eventIds(r: FlagRowUI): string[] {
  const ids = [...r.linkedTransactionIds];
  if (r.source.id) ids.push(`${r.source.table}:${r.source.id}`);
  return ids;
}

function distinctHeadKeys(rows: FlagRowUI[]): FlaggedHeadKey[] {
  return [...new Set(rows.map((r) => r.headKey))];
}

function finalize(
  key: string,
  label: string,
  rows: FlagRowUI[],
  dedupe: boolean,
): FlagGroup {
  const amounts = rows.map((r) => r.amount);
  const recs = rows.map(recoverableOf);
  return {
    key,
    label,
    headKeys: distinctHeadKeys(rows),
    rows,
    amount: dedupe ? Math.max(...amounts) : amounts.reduce((s, a) => s + a, 0),
    recoverable: dedupe ? Math.max(...recs) : recs.reduce((s, a) => s + a, 0),
  };
}

function fieldKey(
  r: FlagRowUI,
  mode: GroupByMode,
): { key: string; label: string } {
  switch (mode) {
    case "counterparty":
      return { key: r.counterpartyName, label: r.counterpartyName };
    case "respondent": {
      // Who the claim would be pursued against — the director(s) for
      // director-liability heads, the transaction counterparty otherwise.
      const respondent = pursuedAgainst(r);
      return { key: respondent, label: respondent };
    }
    case "agent":
      return { key: r.headKey, label: r.headLabel };
    case "risk":
      return r.risk
        ? { key: r.risk, label: RISK_BADGE[r.risk].label }
        : { key: "__unreviewed__", label: "Unreviewed" };
    case "decision":
      return { key: r.decision, label: DECISION_BADGE[r.decision].label };
    default:
      return { key: "all", label: "All" };
  }
}

function clusterByEvent(rows: FlagRowUI[]): FlagGroup[] {
  // Union-find over shared event ids.
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r) ?? r;
    return r;
  };
  const union = (a: string, b: string) => {
    parent.set(find(a), find(b));
  };
  for (const r of rows) parent.set(r.flagId, r.flagId);
  // Map each event id to the first flag that owns it, unioning subsequent ones.
  const idOwner = new Map<string, string>();
  for (const r of rows) {
    for (const id of eventIds(r)) {
      const owner = idOwner.get(id);
      if (owner) union(r.flagId, owner);
      else idOwner.set(id, r.flagId);
    }
  }
  const byRoot = new Map<string, FlagRowUI[]>();
  for (const r of rows) {
    const root = find(r.flagId);
    const bucket = byRoot.get(root) ?? [];
    bucket.push(r);
    byRoot.set(root, bucket);
  }
  return [...byRoot.values()].map((clusterRows) => {
    const head = clusterRows[0];
    const label =
      clusterRows.length > 1
        ? `${head.counterpartyName} — ${head.description.slice(0, 60)}`
        : head.counterpartyName;
    return finalize(head.flagId, label, clusterRows, true);
  });
}

export function groupRows(rows: FlagRowUI[], mode: GroupByMode): FlagGroup[] {
  if (mode === "none") {
    return rows.length ? [finalize("all", "All", rows, false)] : [];
  }
  const groups: FlagGroup[] =
    mode === "event"
      ? clusterByEvent(rows)
      : (() => {
          const buckets = new Map<
            string,
            { label: string; rows: FlagRowUI[] }
          >();
          for (const r of rows) {
            const { key, label } = fieldKey(r, mode);
            const b = buckets.get(key) ?? { label, rows: [] };
            b.rows.push(r);
            buckets.set(key, b);
          }
          return [...buckets.entries()].map(([key, b]) =>
            finalize(key, b.label, b.rows, false),
          );
        })();
  return groups.sort((a, b) => b.recoverable - a.recoverable);
}
